import { useMemo } from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartState } from "./ChartState";
import { formatAxis, rollingAvg } from "./utils";
import { useTimeseries } from "@shared/hooks/useTimeseries";

export function WinRateTrend({ from, useDaily, refreshKey }: { from: number | undefined; useDaily: boolean; refreshKey: number }) {
  const { data, error } = useTimeseries({ metric: "win_rate", bucket: useDaily ? "day" : "match", from, refreshKey });

  const points = useMemo(() => {
    if (!data || data.length === 0) return [];
    const values = data.map((point) => point.v);
    const windowSize = useDaily ? 7 : 10;
    return data.map((point, index) => ({
      t: point.t,
      // null win_rate (no decided match) → gap in the line, not a 0% dip.
      wr: point.v == null ? null : point.v * 100,
      wrAvg: rollingAvg(values, index, windowSize) * 100,
    }));
  }, [data, useDaily]);

  if (data === null) return <ChartState>Loading chart...</ChartState>;
  if (data.length === 0) return <ChartState>{error ? `Could not load chart: ${error}` : "No tracked match data yet."}</ChartState>;

  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={points}>
        <CartesianGrid stroke="#30363d" strokeDasharray="3 3" />
        <XAxis dataKey="t" stroke="#8b949e" tick={{ fontSize: 11 }} tickFormatter={(v) => formatAxis(v, useDaily)} minTickGap={40} />
        <YAxis stroke="#8b949e" tick={{ fontSize: 11 }} width={40} unit="%" domain={[0, 100]} />
        <Tooltip contentStyle={{ background: "#161b22", border: "1px solid #30363d" }} labelFormatter={(v) => formatAxis(v as number | string, useDaily)} formatter={(v: number) => v.toFixed(1) + "%"} />
        <Line type="monotone" dataKey="wr" stroke="#3fb950" strokeOpacity={0.4} dot={false} name="Win %" />
        <Line type="monotone" dataKey="wrAvg" stroke="#3fb950" strokeWidth={2} dot={false} name={useDaily ? "7d avg" : "10-match avg"} />
      </LineChart>
    </ResponsiveContainer>
  );
}
